import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  Crown,
  Settings,
  Folder,
  ChevronRight,
  Box,
  Layers,
} from '@/components/ui/icons';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { CreditsButton } from '@/components/CreditsButton';
import { FETCH_ON_MOUNT } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useQuery } from '@tanstack/react-query';
import { Conversation, ConversationSettings } from '@shared/types';
import { editorUrl } from '@/lib/utils';
import { UserAvatar } from '@/components/chat/UserAvatar';
import { AccountSwitcher } from '@/components/auth/AccountSwitcher';
import { useProfile } from '@/services/profileService';

interface SidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
}

function DesktopSidebar({ isSidebarOpen, setIsSidebarOpen }: SidebarProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const scanToCadEnabled = useFeatureFlag('scan-to-cad', false);
  const meshToStepEnabled = useFeatureFlag('mesh-to-step', false);

  // Panel drops directly from below AppMenuBar (h-10 = 2.5rem); trigger is in AppMenuBar
  const topOffset = '2.5rem';

  const { data: recentConversations } = useQuery<Conversation[]>({
    // Key on the user id so the list refetches once auth resolves; gate on a
    // resolved user so it never queries with an empty user_id (which returns 0
    // rows and, without the id in the key, would stick empty). (see EditorView)
    queryKey: ['conversations', 'recent', user?.id],
    enabled: !!user?.id,
    initialData: [],
    ...FETCH_ON_MOUNT,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .order('updated_at', { ascending: false })
        .eq('user_id', user?.id ?? '')
        .limit(10)
        .overrideTypes<Array<{ settings: ConversationSettings }>>();
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="fixed right-0 z-50" style={{ top: topOffset }}>
      {isSidebarOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[49]"
            onClick={() => setIsSidebarOpen(false)}
          />,
          document.body,
        )}
      {isSidebarOpen && (
        <div
          className="w-64 overflow-y-auto rounded-bl-md border border-t-0 border-telekinesis-neutral-700 bg-telekinesis-bg-dark pb-2 dark:bg-gray-950"
          style={{ maxHeight: `calc(100dvh - ${topOffset})` }}
        >
          {/* Account dropdown */}
          <div className="px-4 pb-2 pt-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex cursor-pointer items-center space-x-3 rounded-md px-2 py-1.5 transition-colors hover:bg-accent-foreground">
                  <UserAvatar />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-telekinesis-text-primary">
                      {profile?.full_name ||
                        user?.email?.split('@')[0] ||
                        'User'}
                    </span>
                    <span className="text-xs text-telekinesis-text-tertiary dark:text-gray-400">
                      {user?.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" side="bottom">
                <div className="flex items-center space-x-2 p-2">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium text-telekinesis-text-primary">
                      {profile?.full_name ||
                        user?.email?.split('@')[0] ||
                        'User'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuGroup className="text-telekinesis-text-primary">
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/subscription')}>
                    <Crown className="mr-2 h-4 w-4" />
                    <span>Subscriptions</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                {/* Multi-account switcher: other accounts, add account, and the
                    per-account / all-accounts sign-out items (#297). */}
                <AccountSwitcher />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Credits */}
          <div className="px-5 pb-3">
            <CreditsButton />
          </div>

          {/* Navigation */}
          <div className="flex min-h-0 flex-1 flex-col px-4 py-2">
            <nav className="space-y-1">
              <Link to="/files">
                <Button
                  variant="tk_dark"
                  onClick={() => navigate('/files')}
                  className="w-full justify-start"
                >
                  <Folder className="mr-2 h-[22px] w-[22px] min-w-[22px]" />
                  <span className="text-[#107d40]">All Files</span>
                  <ChevronRight className="ml-auto h-4 w-4 text-[#107d40]" />
                </Button>
              </Link>

              {scanToCadEnabled && (
                <Link to="/scan-to-cad">
                  <Button
                    variant="tk_dark"
                    onClick={() => {
                      navigate('/scan-to-cad');
                      setIsSidebarOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <Box className="mr-2 h-[22px] w-[22px] min-w-[22px]" />
                    <span className="text-[#107d40]">Scan to CAD</span>
                    <ChevronRight className="ml-auto h-4 w-4 text-[#107d40]" />
                  </Button>
                </Link>
              )}

              {meshToStepEnabled && (
                <Link to="/mesh-to-step">
                  <Button
                    variant="tk_dark"
                    onClick={() => {
                      navigate('/mesh-to-step');
                      setIsSidebarOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <Layers className="mr-2 h-[22px] w-[22px] min-w-[22px]" />
                    <span className="text-[#107d40]">Mesh to STEP</span>
                    <ChevronRight className="ml-auto h-4 w-4 text-[#107d40]" />
                  </Button>
                </Link>
              )}

              {recentConversations && recentConversations.length > 0 && (
                <div className="ml-2 mt-4 space-y-1">
                  <div className="flex h-9 w-full items-center rounded-md px-4 py-2 text-sm font-medium text-telekinesis-text-tertiary">
                    <Folder className="mr-2 h-[22px] w-[22px] min-w-[22px]" />
                    Recent Files
                  </div>
                  <ul className="ml-7 flex list-none flex-col gap-1 border-l border-telekinesis-neutral-500 px-2">
                    {recentConversations.map(
                      (
                        conversation: Omit<
                          Conversation,
                          'message_count' | 'last_message_at'
                        >,
                      ) => (
                        <Link
                          to={editorUrl(conversation.id, conversation.title)}
                          key={conversation.id}
                          onClick={() => setIsSidebarOpen(false)}
                        >
                          <li>
                            <span className="line-clamp-1 text-ellipsis text-nowrap rounded-md p-1 text-xs font-medium text-telekinesis-neutral-400 transition-colors duration-200 ease-in-out [@media(hover:hover)]:hover:bg-telekinesis-neutral-950 [@media(hover:hover)]:hover:text-telekinesis-neutral-10">
                              {conversation.title}
                            </span>
                          </li>
                        </Link>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileSidebar({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isSidebarOpen,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsSidebarOpen,
}: SidebarProps) {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          className="fixed right-2 top-2.5 z-50 flex items-center gap-1.5 px-2 hover:bg-telekinesis-neutral-700 md:hidden"
        >
          <span className="text-sm text-telekinesis-text-primary">
            Account & Files
          </span>
          <Menu className="h-5 w-5 text-telekinesis-text-primary" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="bg-telekinesis-bg-dark p-0 [&>button]:text-white"
      >
        <SheetHeader className="hidden">
          <SheetTitle className="text-telekinesis-text-primary">
            Telekinesis
          </SheetTitle>
          <SheetDescription>
            AI-powered CAD software for everyone
          </SheetDescription>
        </SheetHeader>
        {/* Reuse desktop content with open=true */}
        <DesktopSidebar isSidebarOpen={true} setIsSidebarOpen={setOpen} />
      </SheetContent>
    </Sheet>
  );
}

export function Sidebar({ isSidebarOpen, setIsSidebarOpen }: SidebarProps) {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Guests have no account panel — the menu bar shows Log In / Sign Up buttons
  // instead (see AppMenuBar).
  if (user == null || user.is_anonymous) return <></>;

  return isMobile ? (
    <MobileSidebar
      isSidebarOpen={isSidebarOpen}
      setIsSidebarOpen={setIsSidebarOpen}
    />
  ) : (
    <DesktopSidebar
      isSidebarOpen={isSidebarOpen}
      setIsSidebarOpen={setIsSidebarOpen}
    />
  );
}
